/**
 * Where the live scanner gets its frames: the browser's camera, through a canvas.
 *
 * The other source is the Android shell's (`native-camera.ts`), and the two exist
 * because they are answered by different things: a browser tab has `getUserMedia`
 * and a phone's shell has a real preview, autofocus and a torch. What they share is
 * the seam: **one look returns an observation, one grab returns a `CaptureRead`
 * from the shared core.** Neither decides what a card is.
 *
 * The detector runs here, per look, on a frame a few hundred pixels wide. That is
 * the same `findCardQuad` the still is read with, so the browser cannot promise a
 * card is framed by one rule and then file it by another; the only difference is
 * the resolution it is asked at, which is what makes it cheap enough to run while
 * the viewfinder is live.
 */

import { findCardQuad, quadBounds, type GreyImage, type Quad } from '@unsorted/shared';
import { readCapture, type CaptureRead } from './capture.js';
import { greyLevelsFromRgba } from './decode.js';
import { observationFromQuad, type Observation } from './live.js';

/** How wide the frame the live detector sees is. Small on purpose: it runs per look. */
export const ANALYSIS_WIDTH = 256;

/** The largest edge of the grey image a grabbed still is read at, as `decodeCapture` uses. */
export const STILL_MAX_EDGE = 900;

/** What both sources promise, so the sheet cannot tell them apart. */
export interface CameraSource {
  /** Which camera answered. The native sheet is transparent over the shell's preview. */
  readonly kind: 'browser' | 'native';
  /** Open the camera. Throws with a sentence a person can act on. */
  start(): Promise<{ torch: boolean }>;
  /** Close it, and release the device. Safe to call twice. */
  stop(): Promise<void>;
  /** One look at the frame. Cheap enough to run several times a second. */
  read(): Promise<Observation>;
  /** Take a still and read it with the shared core. Null when there is no frame. */
  grab(): Promise<CaptureRead | null>;
  setTorch(on: boolean): Promise<void>;
  /** Focus and meter at a point in the frame, both coordinates 0..1. */
  focusAt(x: number, y: number): Promise<void>;
}

/** The part of `HTMLVideoElement` this needs, so a test can hand over a stub. */
export interface VideoLike {
  videoWidth: number;
  videoHeight: number;
  /** The same union the DOM uses, so a real element is assignable to this stub. */
  srcObject: MediaStream | Blob | MediaSource | null;
  play(): Promise<void>;
}

/** The part of `navigator.mediaDevices` this needs. */
export interface MediaDevicesLike {
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
}

/** The two frames a look and a capture need, with the canvas hidden behind them. */
export interface BrowserFrameReader {
  /** A frame at roughly `ANALYSIS_WIDTH`, for the live detector. */
  analyse(): GreyImage | null;
  /** The full frame, at the read width, for the fingerprint. */
  still(): GreyImage | null;
}

export interface BrowserCameraSourceOptions {
  video: VideoLike;
  /** Injected in tests: there is no canvas, and no video, in jsdom. */
  frames?: BrowserFrameReader;
  media?: MediaDevicesLike;
}

/**
 * The camera as the page sees it.
 *
 * The constraints ask for the largest frame the device will give, because the card
 * that fills the guide should be more than 300 pixels wide by the time it reaches
 * the art window - the still is read at `FRAME_WIDTH` by the core, and downsampling
 * once from a good frame beats upsampling from a bad one. Autofocus is requested as
 * continuous where the device offers it; a phone that refuses the constraint is
 * still a working camera, so the refusals are swallowed rather than reported.
 */
export function browserCameraSource(options: BrowserCameraSourceOptions): CameraSource {
  const { video } = options;
  const frames = options.frames ?? canvasFrameReader(video);
  const media = options.media ?? (navigator.mediaDevices as MediaDevicesLike | undefined);

  let stream: MediaStream | null = null;
  let track: MediaStreamTrack | null = null;
  let lastQuad: Quad | null = null;
  let torch = false;

  const apply = async (advanced: Record<string, unknown>): Promise<void> => {
    try {
      await track?.applyConstraints({ advanced: [advanced] } as unknown as MediaTrackConstraints);
    } catch {
      // A constraint this camera does not implement is not a failed scan.
    }
  };

  return {
    kind: 'browser',

    async start() {
      if (!media?.getUserMedia) {
        throw new Error('this browser has no camera to open (no getUserMedia here)');
      }
      stream = await media.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 },
        },
      });
      video.srcObject = stream;
      await video.play().catch(() => undefined);
      track = stream.getVideoTracks()[0] ?? null;
      const capabilities = (track?.getCapabilities?.() ?? {}) as { torch?: boolean };
      torch = capabilities.torch === true;
      await apply({ focusMode: 'continuous' });
      return { torch };
    },

    async stop() {
      stream?.getTracks().forEach((each) => each.stop());
      stream = null;
      track = null;
      lastQuad = null;
      video.srcObject = null;
    },

    async read() {
      const frame = frames.analyse();
      if (frame === null) return { card: 'none', steady: false };
      const quad = findCardQuad(frame);
      const bounds = quad === null ? null : quadBounds(quad);
      const coverage = bounds === null ? 0 : (bounds.x1 - bounds.x0) / frame.width;
      const observation = observationFromQuad(quad, coverage, lastQuad, frame.width);
      lastQuad = quad;
      return observation;
    },

    async grab() {
      const frame = frames.still();
      return frame === null ? null : readCapture(frame);
    },

    async setTorch(on) {
      if (!torch) throw new Error('this camera has no light to switch on');
      await apply({ torch: on });
    },

    async focusAt(x, y) {
      await apply({ focusMode: 'single-shot', points: [{ x, y }] });
    },
  };
}

/**
 * A canvas in front of the video: one draw for a look, one for a still.
 *
 * The analysis frame is downsampled to `ANALYSIS_WIDTH` in the draw, which is the
 * cheapest place to do it - the detector's per-pixel work scales with the frame it
 * is handed, and a 1920-wide look would be ten times the work for an answer that
 * cannot be more precise than the guide.
 */
export function canvasFrameReader(
  video: VideoLike,
  options: { analysisWidth?: number; stillEdge?: number } = {},
): BrowserFrameReader {
  const analysisWidth = options.analysisWidth ?? ANALYSIS_WIDTH;
  const stillEdge = options.stillEdge ?? STILL_MAX_EDGE;
  let analysis: HTMLCanvasElement | null = null;
  let still: HTMLCanvasElement | null = null;

  const read = (canvas: HTMLCanvasElement, width: number, height: number): GreyImage | null => {
    if (width < 1 || height < 1) return null;
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (context === null) return null;
    context.drawImage(video as unknown as CanvasImageSource, 0, 0, width, height);
    const { data } = context.getImageData(0, 0, width, height);
    return greyLevelsFromRgba(data, width, height);
  };

  return {
    analyse() {
      const sourceWidth = video.videoWidth;
      const sourceHeight = video.videoHeight;
      if (sourceWidth < 1 || sourceHeight < 1) return null;
      analysis ??= document.createElement('canvas');
      const width = Math.max(1, Math.round(analysisWidth));
      const height = Math.max(1, Math.round((analysisWidth * sourceHeight) / sourceWidth));
      return read(analysis, width, height);
    },

    still() {
      const sourceWidth = video.videoWidth;
      const sourceHeight = video.videoHeight;
      if (sourceWidth < 1 || sourceHeight < 1) return null;
      still ??= document.createElement('canvas');
      const scale = Math.min(1, stillEdge / Math.max(sourceWidth, sourceHeight));
      return read(still, Math.max(1, Math.round(sourceWidth * scale)), Math.max(1, Math.round(sourceHeight * scale)));
    },
  };
}
